<script setup lang="ts">
// PROPS
import type { FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { adaptFormDataToDto, phoneValidatorE164 } from '@lib/shared';
import { z } from 'zod';

import {
    type BackendErrorEntity,
    type ErrorType,
    useApiRequestDirectSalePrice,
    type RequestDirectSalePriceBodyDto,
    type DirectSaleItemEntity,
    useApiUpdateAccountPersonalData,
    useApiPostLead,
    LeadIntent,
} from '~/apiClient';

interface Props {
    directSale: DirectSaleItemEntity;
}

const { directSale } = defineProps<Props>();

// COMPOSABLES
const route = useRoute();
const { data: session, isLoggedIn, getSession } = useAuthUtils();
const { tt, locale } = useTypedI18n();
const { isMobile, isLgOrGreater } = useResponsive();
const { logInfo, logError } = useLogs();
const { notifyGenericError, notifySuccess } = useNotification();
const { openAuthModal } = useModals();
const { getCached, setCached } = useCustomerContactsCache();
const { salesContact } = useSalesContact({ sale: () => directSale });
const { mutateAsync: mutateSubmit, isPending: isSubmitting } = useApiRequestDirectSalePrice();
const { mutateAsync: mutatePersonalData } = useApiUpdateAccountPersonalData();
const { mutateAsync: mutatePostLead } = useApiPostLead();

// MODEL
const open = defineModel<boolean>('open', { default: false });

// DATA
const formSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('sdp.priceRequest.fields.email') })),
    phone: zodRequiredString(tt('validation.msg.required', { field: tt('sdp.priceRequest.fields.phone') })).refine(
        value => !value || phoneValidatorE164(value),
        {
            message: tt('validation.msg.phone'),
        }
    ),
});
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;
const cached = getCached();
const formData = reactive<Partial<FormData>>({
    email: session.value?.user?.email ?? cached?.email ?? '',
    phone: session.value?.user?.phone ?? cached?.phone ?? '',
});
const isEmailDisabled = computed(() => isSubmitting.value || !!session.value?.user?.email);
const isPhoneDisabled = computed(() => isSubmitting.value || !!session.value?.user?.phone);

// HANDLERS
const onClick = (): void => {
    if (isLoggedIn.value && session.value?.user?.email && session.value?.user?.phone) {
        requestPrice({ email: session.value.user.email, phone: session.value.user.phone });

        return;
    }

    open.value = true;
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before price request post', {
        formErrors: errorEvent.errors,
        formData,
    });
};

const onSubmit = (event: FormSubmitEvent<FormData>): void => {
    requestPrice({ email: event.data.email, phone: event.data.phone });
};

const requestPrice = (data: CustomerContacts): void => {
    const body: RequestDirectSalePriceBodyDto = adaptFormDataToDto({
        name: session.value?.user?.firstname,
        email: data.email,
        phone: data.phone,
        salesAgentEmail: salesContact.value?.email,
        language: localeToLanguage(locale.value),
    });

    mutateSubmit({ directSaleId: directSale.id, data: body })
        .then(() => {
            notifySuccess({ title: tt('sdp.priceRequest.successTitle'), description: tt('sdp.priceRequest.successDescription') });
            open.value = false;

            if (!isLoggedIn.value) openAuthModal({ email: data.email, phone: data.phone });

            const cached = getCached();
            if (!cached?.phone && !cached?.email) setCached({ email: data.email, phone: data.phone });
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            notifyGenericError();
            logError('Error posting request price', { body, error: apiError });

            // Fallback: gather lead data if price request fails
            mutatePostLead({
                data: {
                    directSaleId: directSale.id,
                    email: data.email,
                    phone: data.phone,
                    intentType: LeadIntent.DirectSaleBuy,
                    language: localeToLanguage(locale.value),
                    urlPath: route.fullPath,
                },
            });
        })
        .then(() => {
            if (isLoggedIn.value && !session.value?.user.phone) {
                mutatePersonalData({ data: { phone: data.phone } })
                    .then(() => getSession())
                    .catch(updateAccountError =>
                        logError(`Error while saving phone after price request`, {
                            directSaleId: directSale.id,
                            userId: session.value?.user.id,
                            apiError: updateAccountError,
                        })
                    );
            }
        });
};
</script>

<template>
    <UButton
        v-bind="$attrs"
        size="lg"
        :ui="buttonResponsiveSizeUi('lg', 'xl')"
        :label="tt('sdp.priceRequest.cta')"
        block
        :loading="isSubmitting"
        @click="onClick"
    />

    <UModal v-model:open="open" :fullscreen="isMobile" :title="tt('sdp.priceRequest.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <UForm class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
                <p class="text-muted text-center font-medium">{{ tt('sdp.priceRequest.description') }}</p>

                <UFormField class="mb-2 w-full" :label="tt('sdp.priceRequest.fields.email')" name="email" required>
                    <UInput v-model.trim="formData.email" type="email" inputmode="email" :disabled="isEmailDisabled" :autofocus="isLgOrGreater" />
                </UFormField>

                <UFormField :label="tt('sdp.priceRequest.fields.phone')" name="phone" required>
                    <template #hint>
                        <BasePhoneHint />
                    </template>
                    <BasePhoneInput v-model.trim="formData.phone" :disabled="isPhoneDisabled" />
                </UFormField>

                <UButton :label="tt('sdp.priceRequest.sendRequest')" class="mt-4" type="submit" size="xl" :loading="isSubmitting" block />
            </UForm>
        </template>
    </UModal>
</template>
